import React, { useState, useEffect } from 'react';
import { FaFilePdf, FaFileExcel } from 'react-icons/fa';
import {
    Box, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Button
} from '@mui/material';
import { Link } from 'react-router-dom';
import Accordion from './Accordion';
import dayjs from 'dayjs';
import "dayjs/locale/es";
import utc from 'dayjs/plugin/utc';
import useInvoiceService from "../hooks/useInvoiceService";
import useDocumentService from "../hooks/useDocumentService";
import { useSnackbar } from "./SnackbarProvider";
import UploadDocument from "./UploadDocument"
import CreatePdf from "./CreatePdf"
import { formatDate, formatPrice, DecimalTextField, formatText, handleApiError, IconBtn, DatePickerComponent } from "./Utils"
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PaymentIcon from '@mui/icons-material/Payment';

dayjs.extend(utc);

const InvoiceRetentionTable = ({
    isInvoice,
    isClient,
    showBuilding,
    showClientOrProvider,
    title,
    totalAmount,
    paidAmount,
    remainingAmountSum,
    invoices,
    fetch,
    uploadDocument,
    handleOpenDeleteDialog,
    exportParams = null,
    paginate = false,
    totalItems = 0,
    currentPage = 1,
    onPageChange = () => {},
}) => {
    const { payInvoice, payRetention, editRetentionLimitDate, exportInvoices, exportInvoicesPdf } = useInvoiceService();
    const { getDocumentBlob } = useDocumentService();
    const showSnackbar = useSnackbar();

    const [loading, setLoading] = useState(false);
    const [invoicesAmounts, setInvoicesAmounts] = useState({});
    const [retentionsAmounts, setRetentionsAmounts] = useState({});
    const [invoicesErrors, setInvoicesErrors] = useState({});
    const [retentionsErrors, setRetentionsErrors] = useState({});

    const [newDate, setNewDate] = useState({});

    useEffect(() => {
        const initializeRetentionDates = () => {
            const dates = {};
            invoices.forEach((invoice) => {
                dates[invoice.id] = invoice.retention_limit_date;
            });
            setNewDate(dates);
        };
        if (!isInvoice) {
            initializeRetentionDates();
        }
    }, [invoices, isInvoice]);

    const handlePay = async (invoice_id) => {
        try {
            setLoading(true);
            const amounts = isInvoice ? invoicesAmounts : retentionsAmounts;
            const amountToPay = amounts[invoice_id];

            if (!amountToPay || amountToPay <= 0) {
                showSnackbar("Por favor, introduzca un valor válido.", 'error');
                return;
            }
            if (isInvoice) {
                await payInvoice({ invoice_id: invoice_id, amount: amountToPay });
            } else {
                await payRetention({ invoice_id: invoice_id, amount: amountToPay });
            }
            showSnackbar('Pago realizado correctamente', 'success');
            fetch();
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error al realizar el pago.'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleRetentionDateChange = (invoice_id, newValue) => {
        let formattedDate = dayjs(newValue).hour(12).minute(0).second(0);
        formattedDate = formattedDate.utc().format('YYYY-MM-DD');
        setNewDate(prev => ({
            ...prev,
            [invoice_id]: formattedDate
        }));
    };

    const changeRetentionDate = async (invoice_id) => {
        try {
            setLoading(true);
            const retentionDate = newDate[invoice_id];
            if (!retentionDate) {
                showSnackbar("Por favor, seleccione una fecha válida.", 'error');
                return;
            }
            await editRetentionLimitDate({ invoice_id: invoice_id, retention_limit_date: retentionDate });
            showSnackbar('Fecha cambiada correctamente', 'success');
            fetch();
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error al cambiar la fecha.'), 'error');
        } finally {
            setLoading(false);
        }
    };


    const handleAmountChange = (id, value) => {
        const invoicesList = invoices.filter(item => item.id === id)[0];
        let remainingAmount;
        let newErrors = {};

        if (isInvoice) {
            remainingAmount = invoicesList.total_price - (invoicesList.paid_amount ?? 0) - (invoicesList.retention ?? 0);

            if (value <= 0) {
                newErrors[id] = "El monto debe ser mayor a 0.";
            } else if (value > remainingAmount) {
                newErrors[id] = `El monto no puede exceder el restante (${remainingAmount.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 })}€).`;
            } else {
                delete newErrors[id];
            }

            setInvoicesAmounts(prev => ({
                ...prev,
                [id]: value
            }));
            setInvoicesErrors(newErrors);
        } else {
            remainingAmount = invoicesList.retention - (invoicesList.paid_retention ?? 0);

            if (value <= 0) {
                newErrors[id] = "El monto debe ser mayor a 0.";
            } else if (value > remainingAmount) {
                newErrors[id] = `El monto no puede exceder el restante (${remainingAmount.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 })}€).`;
            } else {
                delete newErrors[id];
            }

            setRetentionsAmounts(prev => ({
                ...prev,
                [id]: value
            }));
            setRetentionsErrors(newErrors);
        }
    };
    const triggerDownload = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleExportExcel = async () => {
        try {
            const response = await exportInvoices(exportParams);
            const now = new Date();
            const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            triggerDownload(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Facturas_${ts}.xlsx`);
            showSnackbar('Excel generado correctamente.');
        } catch (e) {
            showSnackbar(handleApiError(e, 'Error generando Excel.'), 'error');
        }
    };

    const handleExportPdf = async () => {
        try {
            const response = await exportInvoicesPdf(exportParams);
            const now = new Date();
            const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            triggerDownload(new Blob([response.data], { type: 'application/pdf' }), `Facturas_${ts}.pdf`);
            showSnackbar('PDF generado correctamente.');
        } catch (e) {
            showSnackbar(handleApiError(e, 'Error generando PDF.'), 'error');
        }
    };

    const handleDownloadDocument = async (documentId, documentName) => {
        try {
            const response = await getDocumentBlob(documentId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', documentName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error al descargar el documento.'), 'error');
        }
    };

    // Determine table headers based on isInvoice
    const headers = isInvoice ? [
        { label: 'Código', width: '7%' },
        ...(isClient ? [] : [{ label: 'Código externo', width: '5%' }]),
        { label: 'Fecha creación', width: '2%' },
        { label: 'Fecha vencimiento', width: '2%' },
        ...(showBuilding ? [{ label: 'Obra', width: '8%' }] : []),
        ...(showClientOrProvider ? [{ label: isClient ? 'Cliente' : 'Proveedor', width: '2%' }] : []),
        { label: 'Estado', width: '5%' },
        { label: 'Importe', width: '8%' },
        { label: 'Retención', width: '8%' },
        { label: 'Pagado', width: '8%' },
        { label: 'Restante', width: '6%' },
        { label: 'Cantidad a pagar', width: '8%' },
        { label: 'Acciones', width: '32%' }
    ] : [
        { label: 'Código Factura', width: '5%' },
        { label: 'Fecha Factura', width: '5%' },
        { label: 'Fecha vencimiento', width: '8%' },
        ...(showBuilding ? [{ label: 'Obra', width: '5%' }] : []),
        ...(showClientOrProvider ? [{ label: isClient ? 'Cliente' : 'Proveedor', width: '5%' }] : []),
        { label: 'Estado', width: '5%' },
        { label: 'Importe', width: '8%' },
        { label: 'Pagado', width: '8%' },
        { label: 'Restante', width: '6%' },
        { label: 'Cantidad a pagar', width: '8%' },
        { label: 'Acciones', width: '27%' }
    ];

    return (
        <Accordion
            title={title}
            paginate={paginate}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={onPageChange}
        >
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: '16px', padding: '8px 16px', marginBottom: '16px',
                backgroundColor: '#f5f5f5', borderRadius: '8px'
            }}>
                <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <Typography variant="h8" component="span">
                        <strong>Total Importe:</strong> {formatPrice(totalAmount)}
                    </Typography>
                    <Typography variant="h8" component="span">
                        <strong>Total Pagado:</strong> {formatPrice(paidAmount)}
                    </Typography>
                    <Typography variant="h8" component="span">
                        <strong>Total Restante:</strong> {formatPrice(remainingAmountSum)}
                    </Typography>
                </Box>
                {exportParams && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconBtn icon={<FaFileExcel />} title="Exportar Excel" variant="outlined" color="success" onClick={handleExportExcel} />
                        <IconBtn icon={<FaFilePdf />} title="Exportar resumen PDF" variant="outlined" color="error" onClick={handleExportPdf} />
                    </Box>
                )}
            </Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {headers.map((header, index) => (
                                <TableCell key={index} style={{ width: header.width }}>
                                    {header.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {invoices.filter(item => !item.disabled).map((item, index) => {
                            const remainingAmount = isInvoice
                                ? item.total_price - (item.paid_amount ?? 0) - (item.retention ?? 0)
                                : item.retention - (item.paid_retention ?? 0);
                            return (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Link to={`/invoices/update/${item.id}`}>
                                            {item.code}
                                        </Link>
                                    </TableCell>
                                    {!isClient && isInvoice && (
                                        <TableCell>
                                            {formatText(item.external_provider_id)}
                                        </TableCell>
                                    )}
                                    <TableCell>{formatDate(item.invoice_date)}</TableCell>
                                    {isInvoice ? (
                                        <TableCell>{formatDate(item.invoice_limit_date)}</TableCell>
                                    ) : (
                                        <TableCell>
                                            <DatePickerComponent
                                                label="Fecha fin retención"
                                                value={newDate[item.id]}
                                                onChange={(newValue) => handleRetentionDateChange(item.id, newValue)}
                                                fullWidth={false}
                                            />
                                        </TableCell>
                                    )}
                                    {showBuilding && (
                                        <TableCell>
                                            {item.building ? (
                                                <Link to={`/buildings/update/${item.building.id}`}>
                                                    {item.building.code || '-'}
                                                </Link>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                    )}
                                    {showClientOrProvider && (
                                        <TableCell>
                                            <Link to={`/${isClient ? 'clients' : 'providers'}/update/${item[isClient ? 'client' : 'provider'].id}`}>
                                                {item[isClient ? 'client' : 'provider'].code}
                                            </Link>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        {isInvoice
                                            ? item.invoice_status === 'paid' ? 'PAGADA' :
                                                item.invoice_status === 'not_paid' ? 'NO PAGADA' :
                                                    item.invoice_status
                                            : item.retention_status === 'paid' ? 'PAGADA' :
                                                item.retention_status === 'not_paid' ? 'NO PAGADA' :
                                                    item.retention_status}
                                    </TableCell>
                                    <TableCell>
                                        {isInvoice
                                            ? formatPrice(item.total_price)
                                            : formatPrice(item.retention)
                                        }
                                    </TableCell>

                                    {isInvoice && (
                                        <TableCell>
                                            {formatPrice(item.retention)}
                                        </TableCell>
                                    )}

                                    <TableCell>
                                        {isInvoice
                                            ? formatPrice(item.paid_amount)
                                            : formatPrice(item.paid_retention)
                                        }
                                    </TableCell>

                                    <TableCell>
                                        {formatPrice(remainingAmount)}
                                    </TableCell>
                                    <TableCell>
                                        <DecimalTextField
                                            type="number"
                                            value={isInvoice ? invoicesAmounts[item.id] || '' : retentionsAmounts[item.id] || ''}
                                            onChange={(e) => handleAmountChange(item.id, e.target.value)}
                                            fullWidth
                                            error={!!(isInvoice ? invoicesErrors[item.id] : retentionsErrors[item.id])}
                                            helperText={isInvoice ? invoicesErrors[item.id] : retentionsErrors[item.id]}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                sx={{ minHeight: 34 }}
                                                onClick={() => handlePay(item.id)}
                                                disabled={remainingAmount <= 0 || !!(isInvoice ? invoicesErrors[item.id] : retentionsErrors[item.id]) || !(isInvoice ? invoicesAmounts[item.id] : retentionsAmounts[item.id]) || loading}
                                            >
                                                Pagar
                                            </Button>
                                            {item.documents && item.documents.length > 0 && (() => {
                                                const activeDocument = item.documents.find(doc => !doc.disabled);
                                                return activeDocument ? (
                                                    <IconBtn
                                                        icon={<DownloadIcon />}
                                                        title="Descargar"
                                                        onClick={() =>
                                                            handleDownloadDocument(activeDocument.id, activeDocument.name)
                                                        }
                                                    />
                                                ) : null;
                                            })()}
                                            {uploadDocument ? (
                                                <UploadDocument
                                                    to={'invoice_id'}
                                                    id={item.id}
                                                    fetch={fetch}
                                                    showDisableDoc={item.documents && item.documents.length > 0}
                                                >
                                                </UploadDocument>
                                            ) : (isInvoice && isClient) ? (
                                                <>
                                                    <CreatePdf
                                                        endpoint={`/invoices/${item.id}/generate-pdf/proform`}
                                                        text="Proforma"
                                                        showLabel
                                                    />
                                                    <CreatePdf
                                                        endpoint={`/invoices/${item.id}/generate-pdf/invoice`}
                                                        text="Factura PDF"
                                                        showLabel
                                                    />
                                                </>
                                            ) : null}
                                            {isInvoice ? (
                                                <IconBtn
                                                    icon={<DeleteIcon />}
                                                    title="Eliminar"
                                                    color="error"
                                                    onClick={() => handleOpenDeleteDialog(item.id, '/invoices/disable')}
                                                />
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    sx={{ backgroundColor: 'green', color: 'white', minHeight: 34 }}
                                                    disabled={loading}
                                                    onClick={() => changeRetentionDate(item.id)}
                                                >
                                                    Cambiar fecha
                                                </Button>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Accordion>
    );
};

export default InvoiceRetentionTable;
